import type { FilterClause, FilterOperator, ListQuery } from './types';

/**
 * Build a JSON:API-compliant query string from a typed ListQuery object.
 *
 * BE conventions (maefwk10 v1.7.0+):
 *   - `type` e route in kebab-case plurale (`/api/erp-employees`, `tasks`).
 *   - Attributi, relationships e nomi di campo in `sort`/`fields[]`/`filter` in camelCase.
 *   - Filter syntax: JADNC function-call expressions `equals(field,'foo')`, NON bracket form.
 *
 * Il builder accetta camelCase ovunque e li passa as-is sul wire.
 */
export function buildQueryString(query?: ListQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();

  if (query.filter) {
    const exprs: string[] = [];
    for (const [field, clause] of Object.entries(query.filter)) {
      const expr = buildFilterExpression(field, clause);
      if (expr) exprs.push(expr);
    }
    if (exprs.length === 1) params.append('filter', exprs[0]);
    else if (exprs.length > 1) params.append('filter', `and(${exprs.join(',')})`);
  }

  if (query.sort?.length) {
    params.append('sort', query.sort.join(','));
  }

  if (query.include?.length) {
    params.append('include', query.include.join(','));
  }

  if (query.fields) {
    for (const [type, fields] of Object.entries(query.fields)) {
      if (fields.length) {
        params.append(`fields[${type}]`, fields.join(','));
      }
    }
  }

  if (query.page) {
    if (query.page.number != null) params.append('page[number]', String(query.page.number));
    if (query.page.size != null) {
      // Auto-clamp page size to BE's hard maximum (100). Beyond → 400.
      const clamped = Math.min(query.page.size, 100);
      params.append('page[size]', String(clamped));
    }
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function quote(v: string | number | boolean): string {
  if (typeof v === 'string') return `'${v.replace(/'/g, "''")}'`;
  return `'${String(v)}'`;
}

function buildFilterExpression(field: string, clause: FilterClause): string | null {
  const entries = Object.entries(clause).filter(([, v]) => v !== undefined && v !== null);
  if (entries.length === 0) return null;
  const parts: string[] = [];
  for (const [op, val] of entries) {
    parts.push(opExpr(field, op as FilterOperator, val as string | number | boolean | Array<string | number>));
  }
  if (parts.length === 1) return parts[0];
  return `and(${parts.join(',')})`;
}

function opExpr(field: string, op: FilterOperator, val: string | number | boolean | Array<string | number>): string {
  switch (op) {
    case 'eq':
      return `equals(${field},${quote(val as string | number | boolean)})`;
    case 'ne':
      return `not(equals(${field},${quote(val as string | number | boolean)}))`;
    case 'lt':
      return `lessThan(${field},${quote(val as string | number | boolean)})`;
    case 'le':
      return `lessOrEqual(${field},${quote(val as string | number | boolean)})`;
    case 'gt':
      return `greaterThan(${field},${quote(val as string | number | boolean)})`;
    case 'ge':
      return `greaterOrEqual(${field},${quote(val as string | number | boolean)})`;
    case 'like':
      return `contains(${field},${quote(val as string)})`;
    case 'in': {
      const arr = Array.isArray(val) ? val : [val];
      return `any(${field},${arr.map((x) => quote(x as string | number)).join(',')})`;
    }
    case 'notIn': {
      const arr = Array.isArray(val) ? val : [val];
      return `not(any(${field},${arr.map((x) => quote(x as string | number)).join(',')}))`;
    }
    case 'isNull':
      return `equals(${field},null)`;
    case 'has':
      return `has(${field})`;
    case 'any':
    case 'all':
      // Reserved for relationship-collection filters; not yet supported.
      return `equals(${field},${quote(val as string | number | boolean)})`;
  }
}
